import React from 'react';
import { Check, Loader2, Zap } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { PricingPlan, SubscriptionData } from '../types';

interface PricingCardProps {
    plan: PricingPlan;
    isYearly: boolean;
    subscription: SubscriptionData | null;
    isActive: boolean;
    isPending: boolean;
    isProcessing: boolean;
    currencySymbol: string;
    onSubscribe: (plan: PricingPlan) => void;
}

export const PricingCard: React.FC<PricingCardProps> = ({
    plan,
    isYearly,
    subscription,
    isActive,
    isPending,
    isProcessing,
    currencySymbol,
    onSubscribe
}) => {
    const isCurrentPlan = (subscription?.planId === plan.id && (isActive || isPending)) || (!subscription && plan.id === 'starter');
    const isPlanPending = isPending && subscription?.planId === plan.id;

    return (
        <div 
            className={cn(
                "relative flex flex-col rounded-3xl p-8 shadow-sm transition-all duration-300 overflow-hidden",
                plan.highlighted 
                    ? "bg-primary text-primary-foreground shadow-2xl ring-1 ring-primary transform lg:-translate-y-4" 
                    : "bg-card ring-1 ring-border hover:shadow-lg"
            )}
        >
            {plan.highlighted && (
                <div className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80 pointer-events-none">
                    <div className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-20 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"></div>
                </div>
            )}
            <div className="p-2 z-10">
                <h3 className={cn("text-xl font-semibold leading-6 flex items-center gap-2", plan.highlighted ? "text-primary-foreground" : "text-foreground")}>
                    {plan.highlighted && <Zap className="w-5 h-5 text-amber-400" fill="currentColor" />}
                    {plan.name}
                </h3>
                <div className="mt-4 flex items-baseline text-5xl font-extrabold tracking-tight">
                    {currencySymbol}
                    {isYearly ? plan.yearlyPrice : plan.monthlyPrice}
                    <span className={cn("ml-1 text-xl font-medium tracking-normal", plan.highlighted ? "text-primary-foreground/70" : "text-muted-foreground")}>
                        /{isYearly ? 'yr' : 'mo'}
                    </span>
                </div>
                {isYearly && plan.tier !== 'free' && (
                    <p className={cn("mt-2 text-sm font-medium", plan.highlighted ? "text-amber-400" : "text-primary")}>
                        {(() => {
                            const original = plan.originalYearlyPrice || (plan.monthlyPrice * 12);
                            if (original > plan.yearlyPrice && plan.yearlyPrice > 0) {
                                const percent = Math.round(((original - plan.yearlyPrice) / original) * 100);
                                return `Save ~${percent}% with yearly billing`;
                            }
                            return "Save with yearly billing";
                        })()}
                    </p>
                )}
                <p className={cn("mt-6 text-base", plan.highlighted ? "text-primary-foreground/80" : "text-muted-foreground")}>
                    {plan.description}
                </p>
            </div>
            <ul className="mt-8 space-y-4 flex-1 z-10 focus:outline-none">
                {plan.features.map((feature) => (
                    <li key={feature} className={cn("flex gap-3 text-sm items-start", plan.highlighted ? "text-primary-foreground/90" : "text-foreground")}>
                        <Check className={cn("h-5 w-5 flex-shrink-0", plan.highlighted ? "text-amber-400" : "text-primary")} />
                        <span>{feature}</span>
                    </li>
                ))}
            </ul>
            
            <button
                onClick={() => onSubscribe(plan)}
                disabled={isProcessing || plan.tier === 'free' || (subscription?.planId === plan.id && isActive) || (!subscription && plan.id === 'starter')}
                className={cn(
                    "mt-8 w-full rounded-xl px-6 py-4 text-center text-sm font-semibold shadow-sm transition-colors z-10 flex items-center justify-center gap-2",
                    plan.tier === 'free' 
                        ? "bg-muted text-muted-foreground ring-1 ring-inset ring-border cursor-not-allowed"
                        : (subscription?.planId === plan.id && isActive)
                            ? "bg-green-500/20 text-green-600 ring-2 ring-green-500/50 cursor-default"
                            : plan.highlighted
                                ? "bg-background text-foreground hover:bg-muted focus-visible:outline-background disabled:opacity-50"
                                : "bg-primary/5 text-primary hover:bg-primary/10 ring-1 ring-inset ring-primary/20 disabled:opacity-50"
                )}
            >
                {isProcessing && plan.tier !== 'free' ? (
                    <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Processing...
                    </>
                ) : isCurrentPlan ? (
                    <>
                        {isPlanPending ? (
                            <>
                                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                Complete Purchase
                            </>
                        ) : (
                            <>
                                <Check className="w-5 h-5" />
                                Current Plan
                            </>
                        )}
                    </>
                ) : (
                    plan.buttonText
                )}
            </button>
        </div>
    );
};
